import React, {useEffect, useState} from 'react';

import {useGate, useStore} from 'effector-react';
import {useTranslation} from 'react-i18next';

import {generatePath, useHistory, useLocation, useParams} from 'react-router-dom';

import {toast} from 'react-toastify';

import {downloadInvoicePDF} from 'api/documents/documents';
import {ReactComponent as UploadIcon} from 'assets/svg/download.svg';

import {BackTo} from 'components/common/BackTo';
import {ModalFirsError} from 'components/common/ModalFirsError';
import {Button, ButtonVariant} from 'components/ui-kit/Button';
import {Icon} from 'components/ui-kit/Icon';
import {Spinner} from 'components/ui-kit/Spinner';
import {Stepper} from 'components/ui-kit/Stepper';
import {SpinnerLayer} from 'components/ui-kit/Table2.0/Table/styled';
import {DOCUMENT_STATUS_COLORS} from 'core/constants/statuses';
import {DocumentStatus} from 'core/dictionaries';
import {
    $invoiceIsRemoved,
    $invoiceState,
    $invoiceStatusInfo,
    $newInvoiceIsDone,
    CreateInvoiceGate,
    setInvoiceState,
    updateInvoiceStatusFx,
} from 'models/documents/invoices/common';
import {$doesInvoiceExist} from 'models/documents/invoices/common/init';
import {
    $forceAllowToSend,
    $invoiceCreationPage,
    $showGroupLinesByGtinModal,
    $taxesAndTotal,
    arrStores,
    setShowGroupLinesByGtinModal,
} from 'models/documents/invoices/create';

import {$everythingReady, commonData$} from 'models/documents/invoices/details';
import {useClipboard} from 'utils/custom-hooks';

import {GroupLinesByGtinModal} from '../common/GroupLinesByGtinModal';

import {ConfirmNavigationModal} from './ConfirmNavigationModal';
import {useNavigationBlocker} from './hooks/useNavigationBlocker';
import {InvoiceDocumentsTab} from './Pages/Documents';
import {InvoiceGeneralInfoTab} from './Pages/GeneralInfo';
import {InvoiceLinesTab} from './Pages/InvoiceLines';
import {TaxesAndTotalTab} from './Pages/TaxesTotal';
import {
    ButtonWIcon,
    CloseAndLinkBack,
    CloseAndLinkBackIcon,
    CloseAndLinkBackText,
    DownloadButtonIcon,
    DownloadButtonSpan,
    HeadControl,
    InfoSection,
    InvoiceId,
    InvoiceIdContainer,
    InvoiceIdCopyButton,
    Label,
    Root,
    Separator,
    Status,
    StatusButtonsWrapper,
} from './styled';

export const CreateInvoiceNew = () => {
    useGate(CreateInvoiceGate);
    const {id, invoiceId}: {id: string | undefined; invoiceId: string | undefined} = useParams();
    const location = useLocation();
    const invoiceIsRemoved = useStore($invoiceIsRemoved);
    const newInvoiceIsDone = useStore($newInvoiceIsDone);
    const history = useHistory();
    const invoiceStatusInfo = useStore($invoiceStatusInfo);
    const currentPage = useStore($invoiceCreationPage);
    const arrStore = useStore(arrStores);
    const commonData = useStore(commonData$);
    const invoiceState = useStore($invoiceState);
    const everythingReady = useStore($everythingReady);
    const doesInvoiceExist = useStore($doesInvoiceExist);
    const taxesAndTotal = useStore($taxesAndTotal);
    const showGroupLinesByGtinModal = useStore($showGroupLinesByGtinModal);
    const forceAllowToSend = useStore($forceAllowToSend);
    const {t} = useTranslation();

    const shouldBlockNavigation =
        !doesInvoiceExist && !taxesAndTotal.allowToSend && !forceAllowToSend;
    const {showModal, handleConfirm, handleCancel} = useNavigationBlocker({
        when: shouldBlockNavigation,
    });

    useEffect(() => {
        setInvoiceState(invoiceId ? 'new' : id ?? null);
    }, []);

    const getColors = (status: DocumentStatus) => ({
        color: DOCUMENT_STATUS_COLORS[status ?? 'DEFAULT'],
    });

    useEffect(() => {
        if (newInvoiceIsDone || invoiceIsRemoved) {
            const setPath = generatePath(
                (location.state as {from: string | undefined})?.from ?? '/documents',
            );
            history.push(setPath);
        }
    }, [newInvoiceIsDone, invoiceIsRemoved]);

    const GEN_INFO = 'pages.documents.invoices.create';

    function showStore() {
        console.log(arrStore);
    }
    const copy = useClipboard();
    const copyCode = () => {
        if (!id) {
            return;
        }
        copy(id);
        toast.success(t('pages.documents.details.idCopied'));
    };

    const downloadXml = () => {
        if (!commonData) {
            return;
        }
        const blob = new Blob([commonData], {type: 'application/xml'});

        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;

        a.download = `${id}.xml`;
        document.body.appendChild(a);
        a.click();

        URL.revokeObjectURL(url);
        document.body.removeChild(a);
    };

    const [downloadPending, setDownloadPending] = useState(false);
    const downloadPDF = async () => {
        try {
            setDownloadPending(true);
            const response = await downloadInvoicePDF(id);
            const url = URL.createObjectURL(response);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${id}.pdf`;
            document.body.appendChild(a);
            a.click();
            URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (e) {
            console.error(e);
        } finally {
            setDownloadPending(false);
        }
    };

    function changeInvoiceStatus(to: DocumentStatus) {
        if (invoiceStatusInfo && id) {
            updateInvoiceStatusFx({
                invoiceId: id,
                status: to,
            });
        }
    }

    return (
        <>
            <ModalFirsError />
            <GroupLinesByGtinModal
                open={showGroupLinesByGtinModal}
                onClose={() => setShowGroupLinesByGtinModal(false)}
            />
            <ConfirmNavigationModal
                show={showModal}
                onConfirm={handleConfirm}
                onCancel={handleCancel}
            />
            <HeadControl>
                <BackTo
                    to={(location.state as {from: string | undefined})?.from ?? '/documents'}
                    isBack=""
                >
                    {t('pages.documents.back')}
                </BackTo>
                <CloseAndLinkBack
                    to={(location.state as {from: string | undefined})?.from ?? '/documents'}
                >
                    <CloseAndLinkBackText>{t(`${GEN_INFO}.buttons.close`)}</CloseAndLinkBackText>
                    <CloseAndLinkBackIcon />
                </CloseAndLinkBack>
            </HeadControl>
            <Root>
                <InfoSection>
                    <Label>invoice</Label>
                    {doesInvoiceExist ? (
                        <InvoiceIdContainer>
                            <InvoiceId>{invoiceState}</InvoiceId>
                            <InvoiceIdCopyButton type="button" onClick={copyCode} />
                        </InvoiceIdContainer>
                    ) : (
                        <></>
                    )}
                    <Separator />
                    {invoiceStatusInfo && (
                        <Status color={getColors(DocumentStatus[invoiceStatusInfo.status]).color}>
                            {t(`dictionaries.documents.status.${invoiceStatusInfo.status}`)}
                        </Status>
                    )}
                    {doesInvoiceExist ? (
                        <Button
                            type="button"
                            style={{width: 'auto'}}
                            variant={ButtonVariant.Outline}
                            isLoading={downloadPending}
                            size="sm"
                            onClick={downloadPDF}
                        >
                            {t('pages.documents.download')}
                            <Icon
                                source={UploadIcon}
                                style={{marginLeft: 10}}
                                width={16}
                                height={16}
                            />
                        </Button>
                    ) : (
                        <></>
                        // <Button onClick={showStore} type="button">
                        //     {t(`${GEN_INFO}.buttons.save`)}
                        // </Button>
                    )}
                    {invoiceStatusInfo && invoiceStatusInfo.status === DocumentStatus['NEW'] && (
                        <StatusButtonsWrapper>
                            <ButtonWIcon
                                actionType="reject"
                                onClick={() => {
                                    changeInvoiceStatus(DocumentStatus['REJECTED']);
                                }}
                                type="button"
                            >
                                <DownloadButtonSpan>
                                    {t(`${GEN_INFO}.buttons.reject`)}
                                </DownloadButtonSpan>
                                <DownloadButtonIcon actionType="reject" />
                            </ButtonWIcon>
                            <ButtonWIcon
                                actionType="accept"
                                type="button"
                                onClick={() => {
                                    changeInvoiceStatus(DocumentStatus['ACCEPTED']);
                                }}
                            >
                                <DownloadButtonSpan>
                                    {t(`${GEN_INFO}.buttons.accept`)}
                                </DownloadButtonSpan>
                                <DownloadButtonIcon actionType="accept" />
                            </ButtonWIcon>
                        </StatusButtonsWrapper>
                    )}
                </InfoSection>
                {everythingReady ? (
                    <Stepper
                        extraStyles="margin-bottom: 20px;"
                        steps={[
                            {
                                label: t(`${GEN_INFO}.steps.one`),
                                component: <InvoiceGeneralInfoTab />,
                                ...(doesInvoiceExist && {
                                    passed: true,
                                }),
                            },
                            {
                                label: t(`${GEN_INFO}.steps.two`),
                                component: <InvoiceDocumentsTab />,
                                ...(doesInvoiceExist && {
                                    passed: true,
                                }),
                            },
                            {
                                label: t(`${GEN_INFO}.steps.three`),
                                component: <InvoiceLinesTab />,
                                ...(doesInvoiceExist && {
                                    passed: true,
                                }),
                            },
                            {
                                label: t(`${GEN_INFO}.steps.four`),
                                component: <TaxesAndTotalTab />,
                                ...(doesInvoiceExist && {
                                    passed: true,
                                }),
                            },
                        ]}
                        activeStep={currentPage}
                        isClickable={doesInvoiceExist}
                    />
                ) : (
                    <SpinnerLayer>
                        <Spinner />
                    </SpinnerLayer>
                )}
            </Root>
        </>
    );
};
